import React, { useRef, useState, useEffect, useCallback } from "react";
import {
  FlatList,
  View,
  Text,
  Dimensions,
  Animated,
  TouchableOpacity,
} from "react-native";
import { AppColors } from "../../utility/AppColors";
import { AppFontStyle } from "../../styles/AppFontStyle";
import {
  CreateResponsiveStyle,
  DEVICE_SIZES,
  useDeviceSize,
} from "rn-responsive-styles";
import ImageComponent from "../ImageComponent";
import { AppImages } from "../../utility/AppImages";
import LiveStream from "../live-streaming-common-components/LiveStream";
import { fetchLiveNowGamesEnhanced } from "../../services/eventsAPIEnhanced";
import { toast } from "react-toastify";
import Separator from "../Separator";
import { StackActions, useNavigation } from "@react-navigation/native";
import { navigateToScreen } from "../../utility/NavigationHelper";
import { AppScreens } from "../../screens/Utility/AppScreens";
import LiveNowSliderSkeleton from "./LiveNowSliderSkeleton";
import Avatar from "../choose-avatar-components/Avatar";

const { width: screenWidth } = Dimensions.get("window");

const LiveNowSlider = ({
  heading,
  userData,
  liveEventsData = [],
  showLoader = false,
}) => {
  const deviceSize = useDeviceSize();
  const flatListRef = useRef(null);

  const detectPlatform = (url) => {
    if (!url) return null;
    if (url.includes("youtube.com") || url.includes("youtube"))
      return "youtube";
    if (url.includes("twitch.tv")) return "twitch";
    return null;
  };
  const styles = useStyles();
  const ITEM_WIDTH =
    deviceSize === DEVICE_SIZES.XS
      ? screenWidth * 0.9
      : deviceSize === DEVICE_SIZES.SM
      ? 360
      : 535;
  const navigation = useNavigation();
  const activeWidth =
    deviceSize === DEVICE_SIZES.XS
      ? 352
      : deviceSize === DEVICE_SIZES.SM
      ? 360
      : deviceSize === DEVICE_SIZES.MD
      ? screenWidth * 0.9
      : deviceSize === DEVICE_SIZES.LG
      ? 546
      : 600;
  const isSmallDevice =
    deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM;

  const itemSpacing = screenWidth * 0.02;
  const fullItemWidth = ITEM_WIDTH + itemSpacing;
  const scrollX = useRef(new Animated.Value(0)).current;
  const [currentIndex, setCurrentIndex] = useState(1);
  const [mobileIndex, setMobileIndex] = useState(0);
  const [isAutoScrolling, setIsAutoScrolling] = useState(false);
  const [infiniteData, setInfiniteData] = useState([]);

  useEffect(() => {
    if (liveEventsData.length > 0) {
      // If only one stream, don't duplicate it - just use the original data
      if (liveEventsData.length === 1) {
        const mappedData = liveEventsData.map((item, index) => ({
          ...item,
          uniqueKey: `${item.eventID}-${index}`,
        }));
        setInfiniteData(mappedData);
      } else {
        // Multiple streams - create infinite scroll
        const lastItem = liveEventsData[liveEventsData.length - 1];
        const firstItem = liveEventsData[0];
        const newInfiniteData = [
          { ...lastItem, uniqueKey: `${lastItem.eventID}-prepend` },
          ...liveEventsData.map((item, index) => ({
            ...item,
            uniqueKey: `${item.eventID}-${index}`,
          })),
          { ...firstItem, uniqueKey: `${firstItem.eventID}-append` },
        ];
        setInfiniteData(newInfiniteData);
      }
    }
  }, [liveEventsData]);

  useEffect(() => {
    if (!isSmallDevice && infiniteData.length > 0 && flatListRef.current) {
      // Only scroll to index 1 if we have infinite data (more than the original data)
      if (
        liveEventsData.length > 1 &&
        infiniteData.length > liveEventsData.length
      ) {
        flatListRef.current.scrollToIndex({
          index: 1,
          animated: false,
        });
        setCurrentIndex(1);
      } else {
        // Single stream case - start at index 0
        setCurrentIndex(0);
      }
    }
  }, [infiniteData, isSmallDevice, liveEventsData.length]);

  useEffect(() => {
    if (isSmallDevice || isAutoScrolling || infiniteData.length === 0) return;

    // Only apply infinite scroll logic if we have more than one stream
    if (liveEventsData.length === 1) return;

    if (currentIndex === 0) {
      setTimeout(() => {
        flatListRef.current?.scrollToIndex({
          index: infiniteData.length - 2,
          animated: false,
        });
        setCurrentIndex(infiniteData.length - 2);
      }, 0);
    } else if (currentIndex === infiniteData.length - 1) {
      setTimeout(() => {
        flatListRef.current?.scrollToIndex({
          index: 1,
          animated: false,
        });
        setCurrentIndex(1);
      }, 0);
    }
  }, [
    currentIndex,
    isAutoScrolling,
    infiniteData.length,
    isSmallDevice,
    liveEventsData.length,
  ]);

  const keyExtractor = useCallback(
    (item) => (isSmallDevice ? item.eventID : item.uniqueKey),
    [isSmallDevice]
  );

  const scrollToNext = useCallback(() => {
    if (!flatListRef.current || infiniteData.length === 0) return;
    const nextIndex = currentIndex + 1;
    flatListRef.current.scrollToIndex({
      index: nextIndex,
      animated: true,
    });
    setCurrentIndex(nextIndex);
  }, [currentIndex, infiniteData.length]);

  const scrollToPrevious = useCallback(() => {
    if (!flatListRef.current || infiniteData.length === 0) return;
    const prevIndex = currentIndex - 1;
    flatListRef.current.scrollToIndex({
      index: prevIndex,
      animated: true,
    });
    setCurrentIndex(prevIndex);
  }, [currentIndex, infiniteData.length]);

  const handleMomentumScrollEnd = useCallback(
    (event) => {
      if (isSmallDevice) return;
      setIsAutoScrolling(false);
      const contentOffsetX = event.nativeEvent.contentOffset.x;
      const index = Math.round(contentOffsetX / fullItemWidth);
      setCurrentIndex(index);
    },
    [fullItemWidth, isSmallDevice]
  );

  const handleScroll = Animated.event(
    [{ nativeEvent: { contentOffset: { x: scrollX } } }],
    { useNativeDriver: true }
  );

  const renderItem = useCallback(
    ({ item, index }) => {
      const isSmallDevice =
        deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM;

      const inputRange = [
        (index - 1) * fullItemWidth,
        index * fullItemWidth,
        (index + 1) * fullItemWidth,
      ];

      const animatedWidth = !isSmallDevice
        ? scrollX.interpolate({
            inputRange,
            outputRange: [ITEM_WIDTH, activeWidth, ITEM_WIDTH],
            extrapolate: "clamp",
          })
        : ITEM_WIDTH;

      const scale = !isSmallDevice
        ? scrollX.interpolate({
            inputRange,
            outputRange: [0.9, 1, 0.9],
            extrapolate: "clamp",
          })
        : 1;

      const opacity = !isSmallDevice
        ? scrollX.interpolate({
            inputRange,
            outputRange: [0.6, 1, 0.6],
            extrapolate: "clamp",
          })
        : 1;

      const marginLeft = !isSmallDevice
        ? //@ts-ignore¯¯
          deviceSize === DEVICE_SIZES.XS
          ? scrollX.interpolate({
              inputRange,
              outputRange: [
                (screenWidth - fullItemWidth) / 3,
                (screenWidth - fullItemWidth) / 2,
                (screenWidth - fullItemWidth) / 3,
              ],
              extrapolate: "clamp",
            })
          : null
        : 0;

      const formatAttendeeCount = (count) => {
        if (count >= 1_000_000) {
          return (count / 1_000_000).toFixed(1).replace(/\.0$/, "") + " M";
        }
        if (count >= 1_000) {
          return (count / 1_000).toFixed(1).replace(/\.0$/, "") + " K";
        }
        return count.toString();
      };

      return (
        <Animated.View
          style={[
            styles.item,
            {
              transform: [{ scale }],
              opacity,
              marginLeft,
              width:
                deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM
                  ? "auto"
                  : "auto",
              alignSelf: "center",
              alignItems:
                deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM
                  ? "flex-start"
                  : null,
            },
          ]}
        >
          <View style={{ width: isSmallDevice ? screenWidth * 0.9 : ITEM_WIDTH }}>
            <View
              style={{
                flexDirection: "row",
                gap: 10,
                alignItems: "center",
              }}
            >
              <View
                style={{
                  padding: 4,
                  borderRadius: 7,
                  backgroundColor: AppColors.red_AF0707,
                  alignSelf: "flex-start",
                }}
              >
                <Text style={AppFontStyle.MEDIUM_ANYBODY_18}>Live</Text>
              </View>
              <ImageComponent
                url={AppImages.icWhiteEye}
                style={{
                  height: isSmallDevice ? 14 : 26,
                  width: isSmallDevice ? 14 : 26,
                }}
              />
              <Text
                style={
                  isSmallDevice
                    ? AppFontStyle.REGULAR_ANYBODY_14
                    : AppFontStyle.REGULAR_ANYBODY_20
                }
              >
                {formatAttendeeCount(item.attendeeCount || 0)}{" "}
                {item.attendeeCount === 1 || !item.attendeeCount
                  ? "Attendee"
                  : "Attendees"}
              </Text>
            </View>
            <View
              style={{
                marginVertical: 16,
                position: "relative",
                alignItems: "center",
              }}
            >
              <View
                style={{
                  flexDirection: "row",
                  gap: 5,
                }}
              >
                <View
                  style={{
                    borderColor: AppColors.appGreenColor_51AF07,
                    borderWidth: 2,
                    position: "relative",
                  }}
                >
                  <LiveStream
                    key={`organizer-${item.uniqueKey || item.eventID}-${index}`}
                    platform={detectPlatform(
                      item.eventStreams?.organizerYoutubeStream
                        ?.liveStreamURL ||
                        item.eventStreams?.organizerTwitchStream?.liveStreamURL
                    )}
                    streamId={
                      item.eventStreams?.organizerYoutubeStream
                        ?.liveStreamURL ||
                      item.eventStreams?.organizerTwitchStream?.liveStreamURL
                    }
                    width={isSmallDevice ? 160 : 265}
                    height={isSmallDevice ? 105 : 173}
                    hideControls={true}
                  />
                  <TouchableOpacity
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: isSmallDevice ? 30 : 50,
                    }}
                    onPress={() => {
                      navigateToScreen(navigation, AppScreens.LiveStreaming, {
                        eventId: item.eventID,
                      });
                    }}
                  >
                    <View
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "transparent",
                        zIndex: 10,
                      }}
                    />
                  </TouchableOpacity>
                </View>
                <View
                  style={{
                    borderColor: AppColors.appGreenColor_51AF07,
                    borderWidth: 2,
                    position: "relative",
                  }}
                >
                  <LiveStream
                    key={`challenger-${
                      item.uniqueKey || item.eventID
                    }-${index}`}
                    platform={detectPlatform(
                      item.eventStreams?.challengerYoutubeStream
                        ?.liveStreamURL ||
                        item.eventStreams?.challengerTwitchStream?.liveStreamURL
                    )}
                    streamId={
                      item.eventStreams?.challengerYoutubeStream
                        ?.liveStreamURL ||
                      item.eventStreams?.challengerTwitchStream?.liveStreamURL
                    }
                    width={isSmallDevice ? 160 : 265}
                    height={isSmallDevice ? 105 : 173}
                    hideControls={true}
                  />
                  <TouchableOpacity
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: isSmallDevice ? 30 : 50,
                    }}
                    onPress={() => {
                      navigateToScreen(navigation, AppScreens.LiveStreaming, {
                        eventId: item.eventID,
                      });
                    }}
                  >
                    <View
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "transparent",
                        zIndex: 10,
                      }}
                    />
                  </TouchableOpacity>
                </View>
              </View>
            </View>
            <View
              style={{
                flexDirection: "row",
                justifyContent: "space-between",
              }}
            >
              <View style={{ flex: 1, marginRight: 10 }}>
                <TouchableOpacity
                  onPress={() => {
                    navigateToScreen(navigation, AppScreens.LiveStreaming, {
                      eventId: item.eventID,
                    });
                  }}
                  style={{ marginBottom: 13 }}
                >
                  <Text
                    style={
                      isSmallDevice
                        ? AppFontStyle.MEDIUM_ANYBODY_16
                        : AppFontStyle.MEDIUM_ANYBODY_28
                    }
                    numberOfLines={3}
                    ellipsizeMode="tail"
                  >
                    {item.eventTitle || "Unknown Event"}
                  </Text>
                </TouchableOpacity>
                <View
                  style={{
                    flexDirection: "row",
                    gap: 13,
                    alignItems: "center",
                    alignSelf: "flex-start",
                  }}
                >
                  <ImageComponent
                    url={item.gameImage || AppImages.icDummyUserPic}
                    style={{
                      height: isSmallDevice ? 24 : 40,
                      width: isSmallDevice ? 24 : 40,
                    }}
                  />
                  <Text
                    style={
                      isSmallDevice
                        ? AppFontStyle.REGULAR_ANYBODY_10
                        : AppFontStyle.REGULAR_ANYBODY_14
                    }
                    numberOfLines={1}
                  >
                    {item.gameTitle || "Unknown Game"}
                  </Text>
                </View>
              </View>
              <View style={{ flexDirection: "row", gap: 20 }}>
                <TouchableOpacity
                  onPress={() => {
                    if (
                      userData?.username ===
                      item.participantDetails?.[0]?.userID
                    ) {
                      navigateToScreen(navigation, AppScreens.Profile);
                    } else {
                      navigateToScreen(navigation, AppScreens.UserProfile, {
                        username: item.participantDetails?.[0]?.userID,
                      });
                    }
                  }}
                >
                  <Avatar
                    src={
                      item.participantDetails?.[0]?.profilePicture ||
                      "https://squadify-avatar.s3.eu-west-2.amazonaws.com/tiger.svg"
                    }
                    variant="liveNowPlayer"
                  />
                  <ImageComponent
                    url={AppImages.streamingEventBadge}
                    style={{
                      height: isSmallDevice ? 15 : 22,
                      width: isSmallDevice ? 30 : 22,
                      position: "absolute",
                      left: "2%",
                      bottom: 0,
                      zIndex: 999,
                    }}
                  />
                  <View
                    style={{
                      position: "absolute",
                      paddingHorizontal: 8,
                      paddingVertical: 3,
                      borderRadius: 7,
                      backgroundColor: "#655AFF",
                      bottom: 0,
                      left: 17,
                    }}
                  >
                    <Text
                      style={
                        deviceSize === DEVICE_SIZES.SM ||
                        deviceSize === DEVICE_SIZES.XS
                          ? AppFontStyle.SEMIBOLD_ANYBODY_10
                          : AppFontStyle.SEMIBOLD_ANYBODY_16
                      }
                    >
                      @{item.participantDetails?.[0]?.userID || "User1"}
                    </Text>
                  </View>
                </TouchableOpacity>
                <ImageComponent
                  url={AppImages.icVsLogo}
                  style={{ height: 61, width: 39 }}
                />
                <TouchableOpacity
                  onPress={() => {
                    if (
                      userData?.username ===
                      item.participantDetails?.[1]?.userID
                    ) {
                      navigateToScreen(navigation, AppScreens.Profile);
                    } else {
                      navigateToScreen(navigation, AppScreens.UserProfile, {
                        username: item.participantDetails?.[1]?.userID,
                      });
                    }
                  }}
                >
                  <Avatar
                    src={
                      item.participantDetails?.[1]?.profilePicture ||
                      "https://squadify-avatar.s3.eu-west-2.amazonaws.com/tiger.svg"
                    }
                    variant="liveNowPlayer"
                  />
                  <View
                    style={{
                      position: "absolute",
                      paddingHorizontal: 8,
                      paddingVertical: 3,
                      borderRadius: 7,
                      backgroundColor: "#655AFF",
                      bottom: 0,
                      right: 16,
                    }}
                  >
                    <Text
                      style={
                        deviceSize === DEVICE_SIZES.SM ||
                        deviceSize === DEVICE_SIZES.XS
                          ? AppFontStyle.SEMIBOLD_ANYBODY_10
                          : AppFontStyle.SEMIBOLD_ANYBODY_16
                      }
                    >
                      @{item.participantDetails?.[1]?.userID || "User2"}
                    </Text>
                  </View>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </Animated.View>
      );
    },
    [
      deviceSize,
      scrollX,
      fullItemWidth,
      activeWidth,
      ITEM_WIDTH,
      userData,
      navigation,
    ]
  );

  const getItemLayout = useCallback(
    (_, index) => ({
      length: fullItemWidth,
      offset: fullItemWidth * index,
      index,
    }),
    [fullItemWidth]
  );

  const getItemLayoutMobile = useCallback(
    (_, index) => ({
      length: ITEM_WIDTH,
      offset: ITEM_WIDTH * index,
      index,
    }),
    [ITEM_WIDTH]
  );

  const onScrollToIndexFailed = useCallback(
    (info) => {
      requestAnimationFrame(() => {
        flatListRef.current?.scrollToOffset({
          offset: info.index * fullItemWidth,
          animated: false,
        });
      });
    },
    [fullItemWidth]
  );

  return (
    <>
      {showLoader ? (
        <LiveNowSliderSkeleton />
      ) : (
        <View>
          <View
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              marginTop: 10,
              alignItems: "center",
              marginBottom: 20,
            }}
          >
            <Text
              style={[
                isSmallDevice
                  ? AppFontStyle.BOLD_ANYBODY_16
                  : AppFontStyle.BOLD_ANYBODY_24,
                { paddingHorizontal: isSmallDevice ? "3.5%" : 0 },
              ]}
            >
              {heading}
            </Text>
            {!isSmallDevice && liveEventsData.length > 1 && (
              <View
                style={{
                  flexDirection: "row",
                  gap: isSmallDevice ? 10 : 20,
                }}
              >
                <TouchableOpacity
                  style={{
                    backgroundColor: AppColors.lightBlack,
                    borderRadius: 25,
                    padding: 6,
                    opacity: 1,
                  }}
                  onPress={scrollToPrevious}
                >
                  <ImageComponent
                    url={AppImages.icArrowLeft}
                    style={{ height: 24, width: 24 }}
                  />
                </TouchableOpacity>
                <TouchableOpacity
                  style={{
                    backgroundColor: AppColors.lightBlack,
                    borderRadius: 25,
                    padding: 6,
                    opacity: 1,
                  }}
                  onPress={scrollToNext}
                >
                  <ImageComponent
                    url={AppImages.icArrowRight}
                    style={{ height: 24, width: 24 }}
                  />
                </TouchableOpacity>
              </View>
            )}
          </View>

          {isSmallDevice ? (
            <FlatList
              horizontal
              data={liveEventsData}
              ref={flatListRef}
              initialScrollIndex={mobileIndex}
              showsHorizontalScrollIndicator={false}
              getItemLayout={getItemLayoutMobile}
              renderItem={renderItem}
              keyExtractor={keyExtractor}
              contentContainerStyle={{
                paddingHorizontal: isSmallDevice ? "3.5%" : 0,
                gap: 25,
              }}
            />
          ) : (
            <FlatList
              ref={flatListRef}
              data={infiniteData}
              horizontal
              renderItem={renderItem}
              keyExtractor={keyExtractor}
              showsHorizontalScrollIndicator={false}
              snapToInterval={fullItemWidth}
              decelerationRate="fast"
              contentContainerStyle={{
                paddingLeft: (screenWidth - fullItemWidth) / 2.5,
              }}
              onScroll={handleScroll}
              onMomentumScrollBegin={() => setIsAutoScrolling(true)}
              onMomentumScrollEnd={handleMomentumScrollEnd}
              getItemLayout={getItemLayout}
              onScrollToIndexFailed={onScrollToIndexFailed}
              style={{ overflow: "hidden" }}
              scrollEventThrottle={8}
              removeClippedSubviews={true}
              maxToRenderPerBatch={3}
              initialNumToRender={2}
              windowSize={2}
              legacyImplementation={false}
            />
          )}
          <View
            style={{
              paddingHorizontal: isSmallDevice ? "3.5%" : 0,
            }}
          >
            <Separator
              color={AppColors.appGrayColor_1C1C1C}
              height={2}
              marginTop={20}
              marginBottom={0}
              marginHorizontal={0}
            />
          </View>
        </View>
      )}
    </>
  );
};

const useStyles = CreateResponsiveStyle(
  {
    item: {
      width: 535,
      justifyContent: "center",
      alignItems: "center",
      overflow: "hidden",
    },
    swipeIndicator: {
      alignItems: "center",
      marginBottom: 10,
      opacity: 0.6,
    },
  },
  {
    [DEVICE_SIZES.MD]: { item: {} },
    [DEVICE_SIZES.SM]: { item: { width: 360 } },
    [DEVICE_SIZES.XS]: { item: { width: screenWidth * 0.9 } },
  }
);

export default LiveNowSlider;
